import { mergeApplicationConfig, ApplicationConfig, inject, ErrorHandler, provideAppInitializer } from '@angular/core';
import { Router } from '@angular/router';
import { FirebaseApp, initializeApp as fbInitializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import * as Sentry from '@sentry/angular';
import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';

import { GoogleTagManagerService } from 'angular-google-tag-manager';

import { appConfig } from './app.config';
import { environment } from '../environments/environment';
import { AnalyticsTrackingService } from './core/analytics-tracking.service';
import { Analytics } from './core/analytics/analytics.service';
import { GTM_PUSHER } from './core/analytics/gtm-pusher.token';
import { AuthService } from './core/auth.service';
import { AppsFlyerLinkAttributionService } from './core/appsflyer-link-attribution.service';

// This is needed because the @solana/web3.js uses Buffer as a global underneath.
// Without it, some solana apis won't work (like sendTransaction).
import { Buffer } from 'buffer';
window.Buffer = Buffer;

/**
 * Configuration client side rendered app only.
 */
const clientConfig: ApplicationConfig = {
  providers: [
    provideLottieOptions({
      player: () => player
    }),
    provideFirebaseApp(() => fbInitializeApp(environment.firebase)),
    provideMessaging(() => getMessaging(inject(FirebaseApp))),
    {
      provide: 'googleTagManagerId',
      useValue: environment.google.tagManagerId
    },
    {
      provide: GTM_PUSHER,
      useExisting: GoogleTagManagerService
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler()
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    /**
     * Client-only initialization for browser-specific attribution tracking.
     * This runs after SSR hydration is complete, ensuring browser APIs
     * (window.location, localStorage, sessionStorage) are available and stable.
     *
     * Captures:
     * - Canonical AppsFlyer Meta web-to-app link context
     * - UTM parameters for purchase and registration analytics
     * - Facebook params (fbclid, fbp) for Meta Conversions API attribution
     */
    provideAppInitializer(() => {
      const appsFlyerLinkAttribution = inject(AppsFlyerLinkAttributionService);
      const analyticsTrackingService = inject(AnalyticsTrackingService);
      appsFlyerLinkAttribution.captureFromCurrentUrl();
      analyticsTrackingService.captureParams();
    }),
    /**
     * The AppsFlyer Web SDK is now loaded + fired by GTM (Custom-HTML
     * tags fed by `window.dataLayer`), so there is no inline SDK to init. Emit
     * a `user_identified` dataLayer signal on every authenticated emission so
     * the GTM tag keeps AppsFlyer's Customer User ID in sync with the Firebase
     * auth state (credits a later app install back to the web acquisition
     * source). Root-scoped subscription, lives for the app lifetime — no
     * teardown.
     */
    provideAppInitializer(() => {
      const analytics = inject(Analytics);
      inject(AuthService).user$.subscribe((user) => {
        if (user?.uid) {
          analytics.identifyForGtm(user.uid);
        }
      });
    })
  ]
};

export const appConfigClient = mergeApplicationConfig(appConfig, clientConfig);
